import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Outlet } from 'react-router-dom';
import { sb } from '../lib/supabase';
import { useSupabaseAuth, useProjectAccess } from '../context/AuthContext';
import WorkspaceSidebar, { PROJECT_PILLARS } from '../components/WorkspaceSidebar';
import BreadcrumbBar from '../components/BreadcrumbBar';
import WorkspaceFrame from '../components/WorkspaceFrame';
import DemoLoginsPanel from '../components/DemoLoginsPanel';
import Loader from '../components/Loader';

/**
 * Layer 2 (project) shell: project-scoped four-pillar sidebar + topbar, with
 * nested pillar pages rendered via <Outlet>. Loads the project and its parent
 * programme once and shares them with children through the outlet context.
 * Screens receive `projects: [project]` so existing project-scoped queries work.
 */
export default function ProjectShell() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile, applyProfilePatch } = useSupabaseAuth();
  const projectAccess = useProjectAccess(id);

  const [project, setProject] = useState(null);
  const [programme, setProgramme] = useState(null);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { data: proj } = await sb
        .from('projects')
        .select('*, programme_statuses(name), programme_stages(name, order_index), programmes(*, organisations(name), programme_statuses(name), programme_stages(name, order_index))')
        .eq('id', id)
        .maybeSingle();
      if (!proj) { setNotFound(true); return; }
      const { data: stageRows } = await sb
        .from('programme_stages')
        .select('id, name, order_index')
        .order('order_index', { ascending: true });
      setProject(proj);
      setProgramme(proj.programmes || null);
      setStages(stageRows || []);
      if (profile?.id && profile.last_project_id !== id) {
        sb.from('profiles').update({ last_project_id: id }).eq('id', profile.id).then(({ error }) => {
          if (!error) applyProfilePatch?.({ last_project_id: id });
        });
      }
    } catch (e) {
      console.error('[ProjectShell] load failed', e);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [id, profile?.id, profile?.last_project_id, applyProfilePatch]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Loader text="Opening project…" />;

  if (notFound || !project) {
    return (
      <div className="screen active">
        <div className="main">
          <div className="content">
            <div className="empty" style={{ marginTop: 40 }}>
              Project not found, or you don't have access.
              <div style={{ marginTop: 12 }}>
                <button className="btn-ghost" onClick={() => navigate('/home')}>← All programmes</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const sidebarRole = projectAccess.isAdmin ? 'admin' : projectAccess.isViewer ? 'viewer' : projectAccess.hasAccess ? 'member' : 'viewer';
  const orgName = programme?.organisations?.name || 'Organisation';
  const currentStageOrder = project.programme_stages?.order_index ?? -1;

  return (
    <div className="screen active">
      <WorkspaceSidebar basePath={`/project/${id}`} pillars={PROJECT_PILLARS} role={sidebarRole} subLabel="Project workspace" />
      <div className="main">
        <BreadcrumbBar
          segments={[
            { label: orgName, onClick: () => navigate('/home') },
            ...(programme ? [{ label: programme.name, onClick: () => navigate(`/programme/${programme.id}`) }] : []),
            { label: project.name, current: true },
          ]}
        />

        <WorkspaceFrame
          rexBase={{
            layer: 'project',
            orgName,
            programmeName: programme?.name,
            projectName: project.name,
            projectStage: project.programme_stages?.name,
          }}
        >
          <div className="topbar">
            <div>
              <div className="tbtitle">{project.name}</div>
              <div className="tbcrumb">{programme?.name || 'Project workspace'}</div>
            </div>
            <div className="badges">
              {programme?.is_demo && <span className="b am">Demo</span>}
              {project.programme_stages?.name && <span className="b">{project.programme_stages.name}</span>}
              {project.programme_statuses?.name && <span className="b gr">{project.programme_statuses.name}</span>}
            </div>
          </div>

          {programme?.is_demo && <DemoLoginsPanel programme={programme} />}

          <Outlet context={{ project, programme, projects: [project], stages, currentStageOrder, reload: load, projectAccess }} />
        </WorkspaceFrame>
      </div>
    </div>
  );
}
